import React, { useEffect, useMemo, useRef, useState } from "react";
import { Autocomplete, Box, IconButton, TextField } from "@mui/material";
import apiClient from "../services/apiClient";
import { useDispatch, useSelector } from "react-redux";
import { MaterialReactTable, useMaterialReactTable } from "material-react-table";
import { MdDelete, MdFilterList, MdFilterListOff } from "react-icons/md";
import PhoneIcon from "@mui/icons-material/Phone";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";

import SearchIcon from "@mui/icons-material/Search";
import PageLoader from "../components/Loading/PageLoader";
import CustomButton from "../components/Button/CustomButton";
import CustomInputBox from "../components/Form/CustomInputBox";
import { setModal } from "../features/modal/ModalSlice";
import { formatDate, formatToSeconds } from "../utils/utils";
import toast from "react-hot-toast";

const CallDashboard = () => {
  const [callLogs, setCallLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [showFilter, setShowFilter] = useState(true);
  const observer = useRef();
  const userInfo = useSelector((state) => state.userInfo.user);
  const dispatch = useDispatch();
  const [filter, setFilter] = useState({
    start_date: "",
    end_date: "",
    call_direction: "",
    callerid: "",
    callednum: "",
    callerid_and_or: "",
    callednum_and_or: "and",
    forward_to: "",
    page: 1,
    limit: 10,
    OrgId: "",
  });

  useEffect(() => {
    fetchCallLogs();
  }, [filter]);

  const fetchCallLogs = async (page = 1) => {
    try {
      setLoading(true);
      let payload = {
        ...filter,
        OrgId: userInfo?.company_domain,
        page,
      };

      if (userInfo?.role === "3") {
        if (!userInfo?.settings?.default_extension?.extension) {
          setHasMore(false);
          return;
        }

        payload = {
          ...payload,
          extension: userInfo?.settings?.default_extension?.extension,
        };
      }

      const response = await apiClient.post(
        `${process.env.REACT_APP_VOIP_SERVER_BASE_URL}/calls`,
        payload
      );
      const newLogs = response?.data || [];

      setCallLogs((prev) => [...prev, ...newLogs]);
      setHasMore(newLogs.length > 0 && newLogs.length === filter.limit);
    } catch (err) {
      console.log(err);
      setHasMore(false);
      toast.error("Network Error");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCall = async (log) => {
    try {
      const payload = {
        uuid: [log],
      };

      await apiClient.delete(`${process.env.REACT_APP_VOIP_SERVER_BASE_URL}/calls/delete`, payload);
      setCallLogs((prevCallLogs) => prevCallLogs.filter((calllog) => calllog?.uuid !== log));
      toast.success("deleted!");
    } catch {
      console.log("Error!");
      toast.error("Opps! Something went wrong");
    }
  };

  const lastLogRef = useRef(null);
  useEffect(() => {
    if (observer.current) observer.current.disconnect();

    observer.current = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && hasMore && !loading) {
        const nextPage = Math.ceil(callLogs.length / filter.limit) + 1;
        if (!hasMore) return;
        fetchCallLogs(nextPage);
      }
    });

    if (lastLogRef.current) {
      observer.current.observe(lastLogRef.current);
    }
  }, [callLogs, loading, hasMore]);

  const handleShowFilter = () => {
    setShowFilter(!showFilter);
  };

  const handleDialCall = (data = null) => {
    dispatch(setModal({ type: "CALL_DIALPAD", data }));
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilter((prevData) => ({ ...prevData, [name]: value }));
    setCallLogs([]);
  };

  const columns = useMemo(
    () => [
      { accessorKey: "call_direction", header: "Type" },
      { accessorKey: "callerid", header: "Caller no." },
      { accessorKey: "callednum", header: "Receiver no." },
      {
        accessorKey: "billseconds",
        header: "Durations",
        Cell: ({ cell }) => {
          const data = cell.getValue();
          return formatToSeconds(data);
        },
      },
      {
        accessorKey: "callstart",
        header: "Date",
        Cell: ({ cell }) => {
          const data = cell.getValue();
          return formatDate(data + " GMT");
        },
      },
    ],
    []
  );

  const table = useMaterialReactTable({
    columns,
    data: callLogs,
    enableColumnOrdering: true,
    enableRowSelection: true,
    enableTopToolbar: false,
    enableBottomToolbar: false,
    enableRowActions: true,
    enablePagination: false,
    positionActionsColumn: "last",
    renderRowActions: ({ row }) => (
      <Box sx={{ display: "flex", flexWrap: "nowrap", gap: "8px" }}>
        <IconButton
          disabled={!row?.original?.recording_path}
          onClick={() =>
            window.open(
              `https://voip-sourcesoft.sourcesoftsolutions.com/${row?.original?.recording_path}`,
              "_blank"
            )
          }
        >
          <PlayArrowIcon />
        </IconButton>
        {userInfo?.role === "3" && (
          <IconButton onClick={() => handleDialCall(row?.original?.callednum)}>
            <PhoneIcon />
          </IconButton>
        )}
        {userInfo?.role === "2" && (
          <IconButton onClick={() => handleDeleteCall(row?.original?.uuid)}>
            <MdDelete />
          </IconButton>
        )}
      </Box>
    ),
    muiTableHeadCellProps: {
      sx: {
        backgroundColor: "#F5F6F8",
      },
    },
    muiTablePaperProps: {
      elevation: 0,
      sx: {
        borderRadius: "12px",
        border: "1px solid #e0e0e0",
      },
    },
  });

  return (
    <>
      {loading && <PageLoader text="Loading Logs" />}

      <div className="pageTitle1 px-5 py-4">
        <h3>Call Logs</h3>
        <p>{callLogs?.length} Available</p>
      </div>

      <Box sx={{ padding: 2 }}>
        <div className="d-flex justify-content-between">
          <div className="w-50">
            <CustomInputBox
              icon={SearchIcon}
              value={filter?.callednum}
              name="callednum"
              onChange={handleFilterChange}
              placeholder="Search Call logs"
              type="text"
              backgroundColor={"#FFFFFF"}
              borderRadius="8px"
            />
          </div>

          <div className="d-flex justify-content-end gap-3 mb-4">
            <CustomButton
              text={(showFilter ? "Hide" : "Show") + " Filter"}
              iconPosition="left"
              icon={showFilter ? <MdFilterListOff /> : <MdFilterList />}
              onClick={handleShowFilter}
              sx={{ 
                backgroundColor: "#FFFFFF",
                color: "#000000",
                borderRadius: "8px",
                padding: "8px 16px",
                border: "1px solid #d9d9d9",
                boxShadow: "none",
                "&:hover": {
                  backgroundColor: "#f7f6ffff",
                  boxShadow: "none",
                }
               }}
            />

            <CustomButton text={"Start Call"} onClick={() => handleDialCall()}
              sx={{ 
                backgroundColor: "#6859f7ff",
                color: "#FFFFFF",
                borderRadius: "8px",
                padding: "8px 16px",
                border: "1px solid #6859f7ff",
                boxShadow: "none",
                "&:hover": {
                  boxShadow: "none",
                }
               }}
            />
          </div>
        </div>

        {showFilter && (
          <div className="d-flex justify-content-start gap-3 mb-4">
            <Autocomplete
              disablePortal
              options={["inbound", "outbound"]}
              name={"call_direction"}
              value={filter?.call_direction}
              onChange={(event, newValue) => {
                setFilter((prev) => ({ ...prev, call_direction: newValue || "" }));
                setCallLogs([]);
              }}
              size="small"
              renderInput={(params) => <TextField
                sx={{ width: 200, backgroundColor: "white", borderRadius: "8px" }}
                {...params} label="Type" />}
            />

            <Autocomplete
              disablePortal
              options={[]}
              size="small"
              renderInput={(params) => <TextField
                sx={{ width: 200, backgroundColor: "white", borderRadius: "8px" }}
                {...params} label="Contact" />}
            />

            <CustomButton text={"Reset"} variant="text"
              sx={{ 
                backgroundColor: "#FFFFFF",
                color: "#000000",
                borderRadius: "8px",
                padding: "8px 16px",
                border: "1px solid #d9d9d9",
                boxShadow: "none",
                "&:hover": {
                  backgroundColor: "#f7f6ffff",
                  boxShadow: "none",
                }
               }}
            />
          </div>
        )}

        <MaterialReactTable table={table} />
        <div ref={lastLogRef} style={{ height: "1px" }} />
      </Box>
    </>
  );
};

export default CallDashboard;
